import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Alert, Button, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faDownload, faSync } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';

const CartaIntencionPage = () => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const ciemId = useSelector(state => state.ciem.ciemId);

    useEffect(() => {
        if (ciemId) {
            fetchCartaIntencion();
        }
    }, [ciemId]);

    const fetchCartaIntencion = async () => {
        try {
            setLoading(true);
            setError(null);
            // Revoke previous object URL to avoid memory leaks
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }

            // Request raw binary data — avoids any base64 / atob issues
            const response = await axiosInstance.get(`/ciem/${ciemId}/carta-intencion`, {
                responseType: 'arraybuffer',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error desconocido';
            setError(`Error al cargar la carta de intención: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!pdfUrl) return;
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `carta-intencion-ciem-${ciemId}.pdf`;
        a.click();
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para ver la carta de intención.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">
                                    <FontAwesomeIcon icon={faFileSignature} className="me-2" />
                                    22. Carta de Intención
                                </h3>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={fetchCartaIntencion}
                                        disabled={loading}
                                        title="Recargar"
                                    >
                                        <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin me-1' : 'me-1'} />
                                        Recargar
                                    </Button>
                                    {pdfUrl && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleDownload}
                                            title="Descargar PDF"
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                                            Descargar
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {error && (
                                <div className="p-3">
                                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                        {error}
                                    </Alert>
                                </div>
                            )}

                            {loading && (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </Spinner>
                                    <p className="mt-3 text-muted">Cargando carta de intención...</p>
                                </div>
                            )}

                            {!loading && !error && !pdfUrl && (
                                <div className="text-center py-5 text-muted">
                                    <FontAwesomeIcon icon={faFileSignature} size="3x" className="mb-3 opacity-50" />
                                    <p>No se encontró carta de intención para este CIEM.</p>
                                </div>
                            )}

                            {!loading && pdfUrl && (
                                <div style={{ width: '100%', height: '80vh' }}>
                                    <iframe
                                        src={pdfUrl}
                                        title="Carta de Intención"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none', display: 'block' }}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CartaIntencionPage;
